import {createContext, RefObject} from 'react'
import {v4 as uuidv4} from 'uuid'
// Type-only: fileMetadata imports REST_SERVER from here, so a value import would be a cycle.
import type {RawFormatHint} from '../../services/fileMetadata'
import type {ShareRoot} from '../../core/storage'
import type {FileTags} from '@/shared/utils/ontology/tags'

// export const REST_SERVER =  "http://localhost:8087/api/test";
export const REST_SERVER = process.env.REACT_APP_API_REST_URL

//process.env.REACT_APPI_REST_URL
export enum REST_ACTIONS {
  METADATA = 'metadata',
  API_URL = 'controller',
}

export type ExternalViewerKey = string
export interface IMessageHandlerContext {
  registerViewer: (key: string, iframe: React.RefObject<HTMLIFrameElement>) => void
  unregisterViewer: (key: string) => void
  sendMessage: (iframe: RefObject<HTMLIFrameElement>, response: HandlerResponse) => void
  // This is just a way to force the implementation of a method to handle the events, but right
  // now it depends on the event listener to call it.
  handleAction: (e: MessageEvent) => void
  requestMode: (key: string, mode: ViewerMode) => void
}

// export type HandlerAction = keyof HandlerActionTable

/**
 * Actions a viewer manager dispatches, by looking up `this[action]`. Adding a name here without a
 * matching method on every manager is a type error, which is the point.
 *
 * Not every viewer→host message belongs here: `requestFilePick` is served once for the whole page by
 * `FilePickerHost`, which listens on `window` itself and replies to `event.source`. Its contract lives
 * in `file-picker/filePickMessages.ts` precisely because no manager should have to know about it.
 */
export type HandlerAction = 'openViewer' | 'closeViewer' | 'requestViewer' | 'resolveDocumentResources' | 'setPrintTitle'

// export type ViewerProps = {
//   id: ExternalViewerKey
//   viewerURL: string
//   viewerParams: ViewerParams
//   mode: ViewerMode
// }

type ObjectInfo = {
  type: string
  id: string
  name: string
  /** Storage URI the object was opened from (e.g. s3://bucket/poolId/file). Set for raw-file viewers. */
  objectUri?: string
  /** "temp" for a transient result; unset for saved objects. */
  origin?: string
}

/** String enum matching OBserver / desktop {@code WorkbenchProperty.name()} values. */
export enum WorkbenchProperty {
  IMPORTANCE_PRIMARY_VIEWER = 'IMPORTANCE_PRIMARY_VIEWER',
  SAVE_ALLOWED = 'SAVE_ALLOWED',
  SAVE_AS_PDF_ALLOWED = 'SAVE_AS_PDF_ALLOWED',
  SAVE_AS_CSV_ALLOWED = 'SAVE_AS_CSV_ALLOWED',
  SAVE_AS_PNG_ALLOWED = 'SAVE_AS_PNG_ALLOWED',
  SAVE_AS_HTML_ALLOWED = 'SAVE_AS_HTML_ALLOWED',
  POSITION_RESULT = 'POSITION_RESULT',
}

/**
 * Where a viewer gets its data from. A capability of the viewer, resolved by the OBserver, so the
 * viewer managers branch on this generic value instead of naming a specific viewer:
 * - CONTROLLER: talk to the OBserver `/controller` endpoint (the default, e.g. .box object viewers).
 * - STORAGE: the browser reads the file straight from storage via a presigned URL (e.g. the
 *   bioinfo-viewer today; any future viewer that downloads its own data can opt in the same way).
 */
export enum ViewerDataSource {
  CONTROLLER = 'CONTROLLER',
  STORAGE = 'STORAGE',
}

type ControllerInfo = {
  controllerClass: string
  controllerEndpoint: string
  objectClass: string
  viewerName: string
  viewerAppName: string
  viewerVersion?: string
  /** {@link ViewerDataSource}; absent (legacy) is treated as CONTROLLER. */
  dataSource?: ViewerDataSource
  /**
   * Ready endpoint path resolved by the OBserver, relative to REST_SERVER (e.g.
   * "controller/<endpoint>"). Preferred over re-deriving from controllerEndpoint when present, so the
   * endpoint shape stays server-authoritative. Absent for STORAGE viewers.
   */
  apiUrl?: string
  workbenchProperties: WorkbenchProperty[]
}

export type ViewerProps = {
  objectInfo: ObjectInfo
  controllerInfo: ControllerInfo
}

export enum ViewerMode {
  READ = 'READ',
  WRITE = 'WRITE',
}

// export type ViewerParams = {
//   exportFlags?: ExportOption[]
// }

// export type ExportOption = 'png' | 'pdf'

// export enum ExportOption {
//   PDF = 'PDF',
//   PNG = 'PNG',
// }

/** Why a viewer tab could not open: no viewer exists for the type, or opening went wrong. */
export type ViewerErrorKind = 'unsupported' | 'failed'

export type ExternalViewerConfig = ViewerProps & {
  viewerURL: string
  mode: ViewerMode
  /**
   * Correlation id linking an optimistic loading placeholder (B1) to the real config that later
   * replaces it. Set on both the placeholder and the resolved config so the manager can reconcile
   * them regardless of the server-assigned objectInfo.id (which differs between the fast-path and
   * the load path). Undefined for the legacy flow.
   */
  correlationId?: string
  /** When true this config is a placeholder — the manager renders a loading spinner, not an iframe. */
  loading?: boolean
  /**
   * Error message to show in the tab when opening failed (B1/B2). Set by replacing a loading
   * placeholder via its correlationId: the tab stays open and shows the error instead of vanishing,
   * so the open is never silently undone by a server failure.
   */
  error?: string
  /**
   * What kind of failure {@link error} is: `'unsupported'` when no viewer exists for the file's type
   * (an expected outcome, shown as a neutral notice), `'failed'` (the default) for anything else.
   */
  errorKind?: ViewerErrorKind
  /**
   * When true the real viewer iframe is mounted but not yet ready to show data, so the manager keeps
   * the loading spinner as an overlay ON TOP of the (hidden) iframe to avoid a blank flash while the
   * viewer bundle downloads and initializes. Cleared once the viewer requests its API URL (i.e. it is
   * ready to load data), revealing the iframe. See B1/#3.
   */
  initializing?: boolean
  /** Message shown by the loading placeholder; callers may configure it (double-click vs actions). */
  loadingMessage?: string
  /**
   * Cloud-root-relative path/key the object was opened from (the `?file=` / openFileInViewer
   * `filename`). Retained so the AI chat can reference the focused object, and so a config restored
   * from storage can re-request its metadata; unset for legacy configs restored from storage before
   * this existed.
   */
  sourcePath?: string
  /**
   * The source file's ontology tags, kept so the tab can show the same type icon as the file
   * manager. Unset for tabs whose file carries no tags and for configs persisted before this existed;
   * those fall back to the extension icon.
   */
  tags?: FileTags
  /**
   * Version of the `/metadata` contract this config was built from — see
   * {@link VIEWER_METADATA_SCHEMA}. Absent on configs persisted before the stamp existed, which is
   * exactly what marks them as needing a refresh when they are restored.
   */
  metadataSchema?: number
  /**
   * Format hint the open flow sent to `/metadata`. Retained so a refresh on restore can take the
   * OBserver's ontology fast-path instead of making it load the object to answer.
   */
  formatHint?: RawFormatHint
  /**
   * Set when the object was opened from a folder someone else shares with the user. `sourcePath` is
   * then relative to that shared folder, the bytes are presigned through the share (never as the
   * user's own key), and `/metadata` is told the share's id. Persisted with the tab so a restored
   * tab keeps reading through the share.
   */
  share?: ShareRoot
}

/**
 * Severity of a {@link NotificationPayload}, matching the OBserver's {@code NotificationSeverity}
 * enum.
 */
export type NotificationSeverity = 'INFO' | 'SUCCESS' | 'WARNING' | 'ERROR'

/**
 * Body of a `showNotification` message — an action or a report link that produced no viewer but has
 * something to tell the user (e.g. "this link only opens in the desktop application"). Mirrors the
 * OBserver's {@code NotificationPayload}; rendered by {@code ViewerNotificationHost}.
 */
export type NotificationPayload = {
  severity?: NotificationSeverity
  title?: string
  message?: string
  details?: Record<string, string>
}

/** One rejected field, mirroring the OBserver's {@code ViewerActionFieldError}. */
export type ViewerActionFieldError = {
  key?: string
  message?: string
}

/**
 * Body of an `actionValidationError` message. Normally intercepted inside the viewer so each message
 * lands on its own wizard field; reaching the host means there was no form left to show it in.
 */
export type ValidationErrorPayload = {
  errors?: ViewerActionFieldError[]
}

/**
 * Body of a `jobLaunched` message — a `JOB`-kind viewer action the OBserver submitted rather than
 * ran, so what comes back is a run id. Mirrors the OBserver's job-launch response and
 * `web-libraries`' {@code ViewerActionJobLaunched}; handled by {@code JobLaunchHost}.
 */
export type JobLaunchedPayload = {
  /** Run id: a WES id, or `mcp-`-prefixed for the mini-job lane. */
  runId?: string
  /** Coarse state at submission, e.g. `QUEUED`. */
  state?: string
  /** CLI tool short name that was submitted. */
  tool?: string
  /** Job display name, for the indicator shown to the user. */
  label?: string
  /** Set when the server refused rather than submitted. */
  error?: string
}

/** Default text for the optimistic loading placeholder tab. */
export const DEFAULT_VIEWER_LOADING_MESSAGE = 'Loading file viewer…'

export interface HandlerActionTable {
  // This no longer returns a response, but maybe we should enforce it
  getApiUrl: (viewerConfig: MessageEvent) => void
  openViewer: (viewerConfig: MessageEvent) => void
  closeViewer: (viewerConfig: MessageEvent) => void
  requestViewer: (viewerConfig: MessageEvent) => void
  /** Reply to the getExportCapabilities probe: which export formats this viewer implements. */
  exportCapabilities: (message: MessageEvent) => void
}

export const MessageHandlerContext = createContext<IMessageHandlerContext>({
  registerViewer: (key: string, iframe: RefObject<HTMLIFrameElement>) => {},
  unregisterViewer: (key: string) => {},
  sendMessage: (iframe: RefObject<HTMLIFrameElement>, response: HandlerResponse) => {},

  handleAction: (e: MessageEvent) => {},
  requestMode: (key: string, mode: ViewerMode) => {},
})

/*
    As mentioned in echarts viewers comments, the requests/responses ideally should
    have an unique key, to be able to match them to a proper callback, rather than passing
    the actiontype, for now (as a proof of concepto with only one action) we use this.
*/
export type HandlerResponse = {
  value?: any
  reqId: string
  action: string
  /**
   * Suggested download filename for export messages. Deliberately not `filepath` — that field means
   * "absolute path to save to" on the desktop, and a bare name there would be written server-side.
   */
  filename?: string
}

function createGenericViewerMessage(
  action: HandlerAction,
  config: ExternalViewerConfig
): HandlerResponse {
  return {
    action: action,
    value: config,
    reqId: uuidv4(),
  }
}

/**
 * Resolve the OBserver API URL a CONTROLLER-backed viewer connects to. Prefers the ready path the
 * OBserver returned (`controllerInfo.apiUrl`, relative to REST_SERVER, or absolute), falling back to
 * re-deriving `controller/<controllerEndpoint>` for legacy responses. Shared by every viewer manager
 * so the endpoint shape stays server-authoritative and consistent across layouts.
 */
export function resolveControllerApiUrl(controllerInfo: {
  apiUrl?: string
  controllerEndpoint: string
}): string {
  const endpoint = controllerInfo.apiUrl ?? `controller/${controllerInfo.controllerEndpoint}`
  return /^https?:\/\//i.test(endpoint) ? endpoint : `${REST_SERVER}/${endpoint}`
}

export function createRequestViewerMessage(config: ExternalViewerConfig): HandlerResponse {
  return createGenericViewerMessage('requestViewer', config)
}

/**
 * Build an `openViewer` message for an optimistic loading placeholder tab (B1). The placeholder's
 * objectInfo.id is the correlationId so it is self-consistent; the real config that arrives later
 * with the same correlationId replaces it in place. controllerInfo/viewerURL are stubs — the manager
 * never renders an iframe for a `loading` config.
 */
export function createOpenLoadingViewerMessage(args: {
  correlationId: string
  name: string
  loadingMessage?: string
}): HandlerResponse {
  const config: ExternalViewerConfig = {
    objectInfo: {type: 'loading', id: args.correlationId, name: args.name},
    controllerInfo: {
      controllerClass: '',
      controllerEndpoint: '',
      objectClass: '',
      viewerName: '',
      viewerAppName: '',
      workbenchProperties: [],
    },
    viewerURL: '',
    mode: ViewerMode.READ,
    correlationId: args.correlationId,
    loading: true,
    loadingMessage: args.loadingMessage,
  }
  return createGenericViewerMessage('openViewer', config)
}

export function createOpenViewerMessage(config: ExternalViewerConfig): HandlerResponse {
  return createGenericViewerMessage('openViewer', config)
}

export function createCloseViewerMessage(config: ExternalViewerConfig): HandlerResponse {
  return createGenericViewerMessage('closeViewer', config)
}

/**
 * Close a viewer/placeholder tab by its objectInfo.id alone (managers match closeViewer on id).
 * Used to tear down an optimistic loading placeholder (B1) when the object turns out to belong on
 * another page.
 */
export function createCloseViewerByIdMessage(id: string): HandlerResponse {
  return {
    action: 'closeViewer',
    value: {objectInfo: {id}},
    reqId: uuidv4(),
  }
}

/**
 * Replace an optimistic loading placeholder (matched by correlationId) with an error state, so the
 * tab stays open and shows what went wrong instead of silently closing (B1/B2). Reuses the openViewer
 * reconciliation path — the config is not `loading`, so the manager's correlationId-replace branch
 * swaps it in place.
 */
export function createViewerErrorMessage(
  correlationId: string,
  name: string,
  errorMessage: string,
  errorKind: ViewerErrorKind = 'failed'
): HandlerResponse {
  const config: ExternalViewerConfig = {
    objectInfo: {type: 'error', id: correlationId, name},
    controllerInfo: {
      controllerClass: '',
      controllerEndpoint: '',
      objectClass: '',
      viewerName: '',
      viewerAppName: '',
      workbenchProperties: [],
    },
    viewerURL: '',
    mode: ViewerMode.READ,
    correlationId,
    error: errorMessage,
    errorKind,
  }
  return createGenericViewerMessage('openViewer', config)
}
